import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { computed, ref, toRef } from 'vue';
import GroupedWeeklyChart from '@/components/charts/GroupedWeeklyChart.vue';
import RecordsWeeklyChart from '@/components/charts/RecordsWeeklyChart.vue';
import SeriesSelector from '@/components/charts/SeriesSelector.vue';
import { ToggleGroup } from '@/components/ui/toggle-group';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { buildWeeklyBins, useGroupedWeeklyAggregation, type GroupedWeeklyRow, type WeeklyBinRange } from '@/composables/records/useGroupedWeeklyAggregation';
import type { RecordWeekBin } from '@/composables/records/useRecordsAggregation';
import { i18n } from '@/plugins/internationalization/i18n';
import colors from '@/plugins/tailwind';
import type { MedicineTreatmentWeek } from '@/types';

/*
 * The Log's treatments per week: one bar per week in the treatment colour, or, per drug, one bar per
 * drug per week with a picker over which drugs are drawn (the top 5 by treatments to begin with), as
 * the delivered feed chart does per Feed Mix. A bar picks its week for the table below; the page
 * owns that selection, this component only reports clicks.
 */
type ChartMode = 'total' | 'byDrug';


export default /*@__PURE__*/_defineComponent({
  __name: 'TreatmentsChart',
  props: {
    weeks: {},
    scopeKey: {},
    selectedWeekStart: {}
  },
  emits: ["week-selected", "selection-reset"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const mode = ref<ChartMode>('total');
const modeOptions = computed(() => [
    { value: 'total' as const, label: i18n.global.t('views.health.inventory.chart.total') },
    { value: 'byDrug' as const, label: i18n.global.t('views.health.inventory.chart.byDrug') },
]);

const rows = computed<GroupedWeeklyRow[]>(() => props.weeks.map((week) => ({ week_start: week.week_start, id: week.drug_id, name: week.drug_name, value: week.treatments })));

// Every week of the picked period is on the axis, quiet ones too, so the chart always reads as the period.
const { periodRange } = useInventoryScope();
const range = computed<WeeklyBinRange | null>(() => (periodRange.value.from && periodRange.value.to ? periodRange.value : null));

/** Every drug's treatments of a week added up. */
const totalBins = computed<RecordWeekBin[]>(() =>
    buildWeeklyBins(rows.value, range.value).map((bin) => ({ weekStart: bin.weekStart, timestamp: bin.timestamp, number: [...bin.valueById.values()].reduce((sum, value) => sum + value, 0) }))
);

const { ranking, selectedIds, series, toggle } = useGroupedWeeklyAggregation(rows, toRef(props, 'scopeKey'), range);

/** Animals treated per week, in all and per drug: what a bar's tooltip adds to its count. */
const animalsByWeek = computed(() => {
    const total = new Map<string, number>();
    const byDrug = new Map<string, number>();

    for (const week of props.weeks) {
        total.set(week.week_start, (total.get(week.week_start) ?? 0) + week.animals);
        byDrug.set(`${week.week_start}|${week.drug_id}`, week.animals);
    }

    return { total, byDrug };
});

function animalsText(animals: number | undefined): string | null {
    return animals ? i18n.global.t('views.health.inventory.chart.animals', { count: i18n.global.n(animals, 'decimal') }) : null;
}

const totalDetail = (weekStart: string) => animalsText(animalsByWeek.value.total.get(weekStart));
const drugDetail = (weekStart: string, drugId: number) => animalsText(animalsByWeek.value.byDrug.get(`${weekStart}|${drugId}`));

const drugsLabel = computed(() => i18n.global.t('views.health.inventory.chart.drugs').replace('%SHOWN%', String(selectedIds.value.size)).replace('%TOTAL%', String(ranking.value.length)));
const seriesName = computed(() => i18n.global.t('views.health.inventory.chart.seriesName'));
const title = computed(() => i18n.global.t(mode.value === 'byDrug' ? 'views.health.inventory.chart.headingByDrug' : 'views.health.inventory.chart.heading'));

return (_ctx: any,_cache: any) => {
  return (mode.value === 'byDrug')
    ? (_openBlock(), _createBlock(GroupedWeeklyChart, {
        key: 0,
        series: _unref(series),
        "selected-week-start": _ctx.selectedWeekStart,
        colors: _unref(colors).chart.treatmentsByDrug,
        title: title.value,
        "series-id-prefix": "drug",
        "point-detail": drugDetail,
        onWeekSelected: _cache[1] || (_cache[1] = ($event: any) => (emit('week-selected', $event))),
        onSelectionReset: _cache[2] || (_cache[2] = ($event: any) => (emit('selection-reset')))
      }, {
        "prepend-actions": _withCtx(() => [
          _createVNode(_unref(ToggleGroup), {
            modelValue: mode.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((mode).value = $event)),
            options: modeOptions.value,
            size: "sm",
            "aria-label": _ctx.$t('views.health.inventory.chart.view')
          }, null, 8, ["modelValue", "options", "aria-label"]),
          _createVNode(SeriesSelector, {
            ranking: _unref(ranking),
            "selected-ids": _unref(selectedIds),
            label: drugsLabel.value,
            onToggle: _unref(toggle)
          }, null, 8, ["ranking", "selected-ids", "label", "onToggle"])
        ]),
        _: 1
      }, 8, ["series", "selected-week-start", "colors", "title"]))
    : (_openBlock(), _createBlock(RecordsWeeklyChart, {
        key: 1,
        bins: totalBins.value,
        "selected-week-start": _ctx.selectedWeekStart,
        color: _unref(colors).chart.treatments,
        title: title.value,
        "series-name": seriesName.value,
        "point-detail": totalDetail,
        onWeekSelected: _cache[4] || (_cache[4] = ($event: any) => (emit('week-selected', $event))),
        onSelectionReset: _cache[5] || (_cache[5] = ($event: any) => (emit('selection-reset')))
      }, {
        "prepend-actions": _withCtx(() => [
          _createVNode(_unref(ToggleGroup), {
            modelValue: mode.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((mode).value = $event)),
            options: modeOptions.value,
            size: "sm",
            "aria-label": _ctx.$t('views.health.inventory.chart.view')
          }, null, 8, ["modelValue", "options", "aria-label"])
        ]),
        _: 1
      }, 8, ["bins", "selected-week-start", "color", "title", "series-name"]))
}
}

})